import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Lock, CheckCircle, XCircle, Loader2, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [email, setEmail] = useState('');
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Verify token on mount
    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setVerifying(false);
                return;
            }

            try {
                const response = await axios.get(`${API}/api/auth/verify-reset-token/${token}`);
                setTokenValid(true);
                setEmail(response.data.email);
            } catch (err) {
                setTokenValid(false);
                setError(err.response?.data?.detail || 'Lien invalide ou expiré');
            } finally {
                setVerifying(false);
            }
        };

        verifyToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Les mots de passe ne correspondent pas');
            return;
        }

        if (password.length < 8) {
            setError('Le mot de passe doit contenir au moins 8 caractères');
            return;
        }

        setLoading(true);

        try {
            await axios.post(`${API}/api/auth/reset-password`, {
                token,
                new_password: password
            });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Une erreur est survenue');
        } finally {
            setLoading(false);
        }
    };

    // Show loading state
    if (verifying) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
                <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-slate-600">Vérification du lien...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-block">
                        <div className="bg-blue-600 rounded-xl p-2 shadow-lg inline-block">
                            <div className="border-2 border-white rounded-lg px-6 py-2">
                                <span className="font-black text-2xl text-white tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                                    PHONEB2B
                                </span>
                            </div>
                        </div>
                    </Link>
                </div>

                <Card className="shadow-xl border-0">
                    <CardHeader className="text-center pb-2">
                        <CardTitle className="text-2xl font-bold text-slate-900">
                            {success ? 'Mot de passe modifié !' : 
                             !token || !tokenValid ? 'Lien invalide' : 
                             'Nouveau mot de passe'}
                        </CardTitle>
                        <CardDescription className="text-slate-500">
                            {success ? 'Vous pouvez maintenant vous connecter' :
                             !token ? 'Aucun token de réinitialisation fourni' :
                             !tokenValid ? error :
                             `Créez un nouveau mot de passe pour ${email}`}
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-4">
                        {success ? (
                            <div className="text-center py-6">
                                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                                </div>
                                <p className="text-slate-600 text-sm mb-6">
                                    Votre mot de passe a été modifié avec succès.
                                </p>
                                <Button 
                                    onClick={() => navigate('/login')}
                                    className="w-full bg-blue-600 hover:bg-blue-700"
                                >
                                    Se connecter
                                </Button>
                            </div>
                        ) : !token || !tokenValid ? (
                            <div className="text-center py-6">
                                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <XCircle className="w-8 h-8 text-red-600" />
                                </div>
                                <p className="text-slate-600 text-sm mb-6">
                                    Ce lien de réinitialisation est invalide ou a expiré.
                                    <br />Veuillez en demander un nouveau.
                                </p>
                                <Link to="/forgot-password">
                                    <Button className="w-full bg-blue-600 hover:bg-blue-700">
                                        Demander un nouveau lien
                                    </Button>
                                </Link>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="password">Nouveau mot de passe</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="pl-10 pr-10"
                                            required
                                            minLength={8}
                                            data-testid="reset-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                    <p className="text-xs text-slate-500">Minimum 8 caractères</p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="confirmPassword"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="pl-10"
                                            required
                                            data-testid="reset-confirm-input"
                                        />
                                    </div>
                                </div>

                                {error && (
                                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                        {error}
                                    </div>
                                )}

                                <Button 
                                    type="submit" 
                                    className="w-full bg-blue-600 hover:bg-blue-700"
                                    disabled={loading}
                                    data-testid="reset-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Modification en cours...
                                        </>
                                    ) : (
                                        'Modifier mon mot de passe'
                                    )}
                                </Button>
                            </form>
                        )}

                        {!success && (
                            <div className="text-center pt-4">
                                <Link 
                                    to="/login" 
                                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                >
                                    <ArrowLeft className="w-4 h-4 inline mr-1" />
                                    Retour à la connexion
                                </Link>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
